import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, CornerDownRight } from "lucide-react";
import { groupMitre, countLabel, attackUrl } from "../../mitre/groupMitre";

const VIOLET = "#8b83f0";
const VIOLET_SOFT = "rgba(139,131,240,0.16)";
const VIOLET_BORDER = "rgba(139,131,240,0.30)";

export default function MitreMappingTree({ mitre }) {
  const groups = useMemo(
    () => groupMitre(mitre).filter((g) => g.id !== "UNKNOWN"),
    [mitre],
  );
  const label = useMemo(() => countLabel(groups), [groups]);
  const presentTactics = useMemo(
    () => new Set(groups.map((g) => g.id)),
    [groups],
  );
  const [open, setOpen] = useState(() =>
    Object.fromEntries(groups.map((g) => [g.id, true])),
  );

  if (groups.length === 0) {
    return (
      <p className="text-xs text-(--color-text-muted) px-4 py-6">
        No MITRE ATT&amp;CK mappings for this box.
      </p>
    );
  }

  return (
    <div className="px-4 pb-4 pt-4">
      {/* count */}
      <p className="text-[10px] font-mono tracking-wide text-(--color-text-muted) mb-4">
        {label}
      </p>

      {/* rail */}
      <div className="relative pl-1">
        {/* glowing spine */}
        <span
          aria-hidden
          className="absolute left-4.25 top-3 bottom-3 w-0.5 rounded-full opacity-50"
          style={{
            background: `linear-gradient(180deg, transparent, var(--color-primary) 8%, ${VIOLET} 92%, transparent)`,
          }}
        />
        {/* energy pulse traveling down the spine */}
        <span
          aria-hidden
          className="mm-pulse absolute left-3.5 w-2 h-2 rounded-full"
          style={{
            background: "var(--color-primary)",
            boxShadow:
              "0 0 12px 3px var(--color-shadow-primary, rgba(34,224,122,.7))",
          }}
        />

        {groups.map((g, i) => {
          const techCount = g.techniques.reduce(
            (n, t) => n + (t.subs.length || 1),
            0,
          );
          const isOpen = open[g.id] ?? true;
          return (
            <section key={g.id} className="relative pl-12 pb-2 mb-1">
              {/* node */}
              <span
                aria-hidden
                className="absolute left-1 top-0.5 w-6.5 h-6.5 rounded-full grid place-items-center text-[11px] font-bold z-2"
                style={{
                  color: "#04150b",
                  background:
                    "radial-gradient(circle at 35% 30%, #6bf5ab, var(--color-primary))",
                  boxShadow:
                    "0 0 0 4px var(--color-primary-50, rgba(34,224,122,.12)), 0 0 16px var(--color-shadow-primary, rgba(34,224,122,.4))",
                }}
              >
                {i + 1}
              </span>

              {/* tactic header (click to collapse) */}
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpen((o) => ({ ...o, [g.id]: !isOpen }))}
                className="w-full flex items-baseline gap-2.5 py-1 text-left group"
              >
                {isOpen ? (
                  <ChevronDown className="w-3.5 h-3.5 self-center shrink-0 text-(--color-text-muted)" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 self-center shrink-0 text-(--color-text-muted)" />
                )}
                <a
                  href={attackUrl(g.id)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="font-mono text-[10px] text-(--color-text-muted) hover:text-(--color-primary)"
                >
                  {g.id}
                </a>
                <span className="text-[12px] font-bold text-(--color-text-primary) tracking-[0.01em]">
                  {g.name}
                </span>
                <span className="ml-auto text-[10px] text-(--color-text-muted) self-center">
                  {techCount} technique{techCount === 1 ? "" : "s"}
                </span>
              </button>

              {/* techniques */}
              {isOpen && (
                <div className="mt-1">
                  {g.techniques.map((t) => {
                    const alsoIn = t.tactics.filter(
                      (x) => x !== g.id && presentTactics.has(x),
                    );
                    return (
                      <div
                        key={`${g.id}-${t.id}`}
                        className="relative mt-2 rounded-[11px] px-3.5 py-2.5"
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(34,224,122,.05), rgba(34,224,122,.02))",
                          border: "1px solid rgba(34,224,122,.16)",
                        }}
                      >
                        {/* connector elbow into the spine */}
                        <span
                          aria-hidden
                          className="absolute top-5 -left-7.5 w-6.5 h-0.5"
                          style={{
                            background:
                              "linear-gradient(90deg, transparent, rgba(34,224,122,.5))",
                          }}
                        />
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <a
                            href={attackUrl(t.id)}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono text-[11px] text-(--color-primary) hover:underline"
                          >
                            {t.id}
                          </a>
                          <span className="text-[11.5px] font-semibold text-(--color-text-primary)">
                            {t.name}
                          </span>
                          {alsoIn.length > 0 && (
                            <span
                              className="text-[9.5px] font-medium px-2 py-0.5 rounded-full"
                              style={{
                                background: VIOLET_SOFT,
                                color: VIOLET,
                                border: `1px solid ${VIOLET_BORDER}`,
                              }}
                              title="Mapped to more than one tactic in this box"
                            >
                              also in {alsoIn.join(", ")}
                            </span>
                          )}
                        </div>

                        {/* sub-techniques */}
                        {t.subs.map((s) => (
                          <div
                            key={s.id}
                            className="flex items-center gap-2 mt-2 ml-1 pl-3.5 text-[11px] border-l border-dashed border-(--color-border)"
                          >
                            <CornerDownRight className="w-3 h-3 shrink-0 text-(--color-text-muted)" />
                            <a
                              href={attackUrl(s.id)}
                              target="_blank"
                              rel="noreferrer"
                              className="font-mono text-[10.5px] text-(--color-text-light-green) hover:underline"
                            >
                              {s.id}
                            </a>
                            <span className="text-(--color-text-primary)">
                              {s.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* keyframes for the traveling pulse (scoped by class name) */}
      <style>{`
        @keyframes mmTravel {
          0%   { top: 8px;  opacity: 0; }
          8%   { opacity: 1; }
          92%  { opacity: 1; }
          100% { top: calc(100% - 16px); opacity: 0; }
        }
        .mm-pulse { animation: mmTravel 5.5s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .mm-pulse { display: none; } }
      `}</style>
    </div>
  );
}